import { logService, LOG_CATEGORIES } from '@/services/logService'
import { settingsService } from '@/services/settingsService'
import { UI_SETTING_KEYS } from '@/services/uiSettingsRegistry'

const SNAPSHOT_VERSION = 1

type ActivePanel = 'tourDetail' | 'workspaceDetail' | 'poiDetail' | null

export type MapViewportSnapshot = {
  center: [number, number]
  zoom: number
  pitch: number
  bearing: number
}

export type UiChromeSnapshot = {
  isSidebarVisible?: boolean
  isLayersPanelVisible?: boolean
  activePanel?: ActivePanel
  isMobileBottomPanelExpanded?: boolean
  isInWorkspacesOverview?: boolean
  autoViewportAdaptationEnabled?: boolean
}

export type SlideshowSnapshot = {
  isOpen: boolean
  currentPhotoId: string | null
}

export type RouteSnapshot = {
  fullPath: string
  workspaceId?: string | null
  photoId?: string | null
  tourId?: string | null
}

export type UiStateSnapshot = {
  version: number
  updatedAt: number
  route?: RouteSnapshot
  ui?: UiChromeSnapshot
  slideshow?: SlideshowSnapshot
  /** Locate-control follow mode was active when the app was last suspended. */
  followLocation?: boolean
  mapViewports: Record<string, MapViewportSnapshot>
}

const DEFAULT_SNAPSHOT: UiStateSnapshot = {
  version: SNAPSHOT_VERSION,
  updatedAt: Date.now(),
  slideshow: { isOpen: false, currentPhotoId: null },
  mapViewports: {},
}

const normalizeSnapshot = (snapshot: unknown): UiStateSnapshot => {
  if (!snapshot || typeof snapshot !== 'object') {
    return { ...DEFAULT_SNAPSHOT, mapViewports: {} }
  }

  const candidate = snapshot as Partial<UiStateSnapshot>
  if (candidate.version !== SNAPSHOT_VERSION) {
    return { ...DEFAULT_SNAPSHOT, mapViewports: {} }
  }

  return {
    ...DEFAULT_SNAPSHOT,
    ...candidate,
    mapViewports: { ...(candidate.mapViewports ?? {}) },
  }
}

class UiStatePersistenceService {
  private snapshot: UiStateSnapshot | null = null

  private ensureSnapshot(): UiStateSnapshot {
    if (this.snapshot) return this.snapshot

    try {
      const stored = settingsService.getSetting<UiStateSnapshot>(UI_SETTING_KEYS.mapUiState)
      this.snapshot = normalizeSnapshot(stored)
    } catch (error) {
      logService.error(LOG_CATEGORIES.GENERAL, 'Failed to load UI state snapshot', { error })
      this.snapshot = { ...DEFAULT_SNAPSHOT, mapViewports: {} }
    }

    return this.snapshot
  }

  private persist(): void {
    if (!this.snapshot) return

    const payload: UiStateSnapshot = {
      ...this.snapshot,
      updatedAt: Date.now(),
    }

    try {
      settingsService.saveSetting(UI_SETTING_KEYS.mapUiState, payload)
      this.snapshot = payload
    } catch (error) {
      logService.error(LOG_CATEGORIES.GENERAL, 'Failed to persist UI state snapshot', { error })
    }
  }

  updateRoute(route: RouteSnapshot): void {
    const snapshot = this.ensureSnapshot()
    snapshot.route = { ...snapshot.route, ...route }
    this.persist()
  }

  updateUiState(partial: UiChromeSnapshot): void {
    const snapshot = this.ensureSnapshot()
    snapshot.ui = { ...snapshot.ui, ...partial }
    this.persist()
  }

  updateSlideshowState(state: SlideshowSnapshot): void {
    const snapshot = this.ensureSnapshot()
    snapshot.slideshow = { ...state }
    this.persist()
  }

  updateMapViewport(key: string, viewport: MapViewportSnapshot): void {
    const snapshot = this.ensureSnapshot()
    snapshot.mapViewports[key] = viewport
    this.persist()
  }

  setFollowLocationActive(active: boolean): void {
    const snapshot = this.ensureSnapshot()
    if (snapshot.followLocation === active) return
    snapshot.followLocation = active
    this.persist()
  }

  isFollowLocationActive(): boolean {
    return this.ensureSnapshot().followLocation === true
  }

  getSnapshot(): UiStateSnapshot {
    const snapshot = this.ensureSnapshot()
    return { ...snapshot, mapViewports: { ...snapshot.mapViewports } }
  }

  getMapViewport(key: string): MapViewportSnapshot | undefined {
    const snapshot = this.ensureSnapshot()
    return snapshot.mapViewports[key]
  }

  flush(): void {
    this.persist()
  }
}

export const uiStatePersistenceService = new UiStatePersistenceService()
